import { useEffect, useState, lazy, Suspense } from "react";
import { Switch, Route, useLocation } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AdPopup } from "@/components/AdPopup";
import { DrabpeLoader } from "@/components/DrabpeLoader";
import { CookieConsent } from "@/components/CookieConsent";
import { SiteMeta } from "@/components/SiteMeta";
import { useAuth } from "@/hooks/use-auth";

// Pages
const Home = lazy(() => import("@/pages/Home").then((m) => ({ default: m.Home })));
const Directory = lazy(() => import("@/pages/Directory").then((m) => ({ default: m.Directory })));
const BusinessDetail = lazy(() => import("@/pages/BusinessDetail").then((m) => ({ default: m.BusinessDetail })));
const AuthPage = lazy(() => import("@/pages/Auth").then((m) => ({ default: m.AuthPage })));
const SubmitBusiness = lazy(() => import("@/pages/SubmitBusiness").then((m) => ({ default: m.SubmitBusiness })));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard").then((m) => ({ default: m.AdminDashboard })));
const MyListings = lazy(() => import("@/pages/MyListings").then((m) => ({ default: m.MyListings })));
const Notifications = lazy(() => import("@/pages/Notifications").then((m) => ({ default: m.Notifications })));
const Advertise = lazy(() => import("@/pages/Advertise").then((m) => ({ default: m.Advertise })));
const Services = lazy(() => import("@/pages/Services").then((m) => ({ default: m.Services })));
const MentorMatch = lazy(() => import("@/pages/MentorMatch").then((m) => ({ default: m.MentorMatch })));
const Resources = lazy(() => import("@/pages/Resources").then((m) => ({ default: m.Resources })));
const Settings = lazy(() => import("@/pages/Settings").then((m) => ({ default: m.Settings })));
const Terms = lazy(() => import("@/pages/Terms").then((m) => ({ default: m.Terms })));
const Privacy = lazy(() => import("@/pages/Privacy").then((m) => ({ default: m.Privacy })));
const DirectorySupport = lazy(() => import("@/pages/DirectorySupport").then((m) => ({ default: m.DirectorySupport })));
const NotFound = lazy(() => import("@/pages/not-found"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));

function ProtectedRoute({ component: Component }: { component: React.ComponentType }) {
  const { user, isLoading } = useAuth();
  const [location, setLocation] = useLocation();

  useEffect(() => {
    if (!isLoading && !user) {
      const redirect = encodeURIComponent(location);
      setLocation(`/login?redirect=${redirect}`);
    }
  }, [isLoading, user, location, setLocation]);

  if (isLoading) return <DrabpeLoader />;
  if (!user) return null;
  return <Component />;
}

function Router() {
  return (
    <Switch>
      <Route path="/" component={Home} />
      <Route path="/directory" component={Directory} />
      <Route path="/business/:id" component={BusinessDetail} />
      <Route path="/login" component={AuthPage} />
      <Route path="/register" component={AuthPage} />
      <Route path="/forgot-password" component={ForgotPassword} />
      <Route path="/reset-password" component={ForgotPassword} />
      <Route path="/submit" component={SubmitBusiness} />
      <Route path="/admin" component={AdminDashboard} />
      <Route path="/my-listings" component={MyListings} />
      <Route path="/notifications" component={Notifications} />
      <Route path="/advertise" component={Advertise} />
      <Route path="/services" component={() => <ProtectedRoute component={Services} />} />
      <Route path="/mentor-match" component={() => <ProtectedRoute component={MentorMatch} />} />
      <Route path="/resources" component={() => <ProtectedRoute component={Resources} />} />
      <Route path="/settings" component={Settings} />
      <Route path="/terms" component={Terms} />
      <Route path="/privacy" component={Privacy} />
      <Route path="/directory-support" component={DirectorySupport} />
      <Route component={NotFound} />
    </Switch>
  );
}

function App() {
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsReady(true), 450);
    return () => clearTimeout(timer);
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <SiteMeta />
        <AdPopup />
        <CookieConsent />
        {isReady ? (
          <Suspense fallback={<DrabpeLoader />}>
            <Router />
          </Suspense>
        ) : (
          <DrabpeLoader />
        )}
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
